<template>
  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">CBHI Member Card</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/members-all">Members</router-link></li>
                    <li class="breadcrumb-item"><router-link :to="`/members/${cbhiNumber}`">{{ memberFullName }}</router-link></li>
                    <li class="breadcrumb-item active">Card</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            {{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            {{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading member card...</p>
          </div>

          <!-- Main Content -->
          <div v-else class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <!-- Card Status Alert -->
                  <div v-if="!isCardIssued" class="alert alert-warning mb-4">
                    <div class="d-flex align-items-center">
                      <i class="bx bx-info-circle fs-2 me-3"></i>
                      <div>
                        <h6 class="alert-heading mb-1">Card Not Yet Issued</h6>
                        <p class="mb-0">
                          This CBHI card has not been issued to the member yet.
                          After printing or downloading, please mark the card as issued.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div v-else class="alert alert-success mb-4">
                    <div class="d-flex align-items-center">
                      <i class="bx bx-check-circle fs-2 me-3"></i>
                      <div>
                        <h6 class="alert-heading mb-1">Card Issued</h6>
                        <p class="mb-2">
                          <strong>Issue Date:</strong> {{ formatDate(cardIssueDate) }}<br>
                          <strong>Issued By:</strong> {{ cardIssuedBy }}<br>
                          <strong v-if="cardIssueNotes">Notes:</strong> {{ cardIssueNotes }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Member Info Summary -->
                  <div class="row mb-4">
                    <div class="col-md-6">
                      <h5>Member Information</h5>
                      <table class="table table-sm">
                        <tbody>
                        <tr>
                          <td><strong>Full Name:</strong></td>
                          <td>{{ memberFullName }}</td>
                        </tr>
                        <tr>
                          <td><strong>CBHI Number:</strong></td>
                          <td class="text-primary fw-bold">{{ memberInfo.cbhiNumber }}</td>
                        </tr>
                        <tr>
                          <td><strong>Plan:</strong></td>
                          <td>
                            <span class="badge bg-primary">{{ planInfo.plan_name }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Coverage:</strong></td>
                          <td>ETB {{ formatCurrency(planInfo.annual_coverage_limit) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <h5>Household Coverage</h5>
                      <table class="table table-sm">
                        <tbody>
                        <tr>
                          <td><strong>Total Members:</strong></td>
                          <td>{{ totalMembers }}</td>
                        </tr>
                        <tr>
                          <td><strong>Beneficiaries:</strong></td>
                          <td>{{ beneficiaryCount }}</td>
                        </tr>
                        <tr>
                          <td><strong>Valid Until:</strong></td>
                          <td>{{ formatDate(memberInfo.expiryDate) }}</td>
                        </tr>
                        <tr>
                          <td><strong>Status:</strong></td>
                          <td>
                                <span class="badge" :class="getStatusBadgeClass()">
                                  {{ capitalizeFirst(memberInfo.status) }}
                                </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Card Component -->
                  <CBHICard
                      ref="cbhiCard"
                      :member-info="memberInfo"
                      :plan-info="planInfo"
                      :beneficiary-count="beneficiaryCount"
                      :issue-date="cardIssueDate || today"
                      @mark-as-issued="handleMarkAsIssued"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>

  <!-- Mark as Issued Modal -->
  <div class="modal fade" id="markIssuedModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-primary">
          <h5 class="modal-title text-white">
            <i class="bx bx-check-circle me-2"></i>
            Mark Card as Issued
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <p class="mb-3">
            Are you sure you want to mark this CBHI card as issued to
            <strong>{{ memberFullName }}</strong>?
          </p>

          <div class="mb-3">
            <label class="form-label">Card Issue Notes (Optional)</label>
            <textarea
                v-model="issueNotes"
                class="form-control"
                rows="3"
                placeholder="e.g., Card serial number, collection date, or any relevant notes..."
            ></textarea>
          </div>

          <div class="alert alert-info mb-0">
            <i class="bx bx-info-circle me-2"></i>
            This will update the member's record and cannot be easily undone.
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="confirmMarkAsIssued" :disabled="processing">
              <span v-if="processing">
                <span class="spinner-border spinner-border-sm me-2"></span>
                Processing...
              </span>
            <span v-else>
                <i class="bx bx-check me-2"></i>
                Confirm Issue
              </span>
          </button>
        </div>
      </div>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import CBHICard from "@/components/cbhi-member-management/CBHICard.vue";
import { authUrl, executeJson } from "@/api";

export default {
  name: "MemberCardView",
  components: {
    NavigationBar,
    CBHICard
  },

  data() {
    return {
      loading: false,
      processing: false,
      cbhiNumber: null,
      memberInfo: {},
      planInfo: {},
      beneficiaryCount: 0,
      issueNotes: '',

      alert: {
        success: false,
        failed: false
      },
      message: '',

      today: new Date().toISOString().split('T')[0]
    };
  },

  computed: {
    memberFullName() {
      return this.memberInfo.fullName || this.memberInfo.full_name || 'Unknown Member';
    },

    isCardIssued() {
      return this.memberInfo.cbhiCardIssued === 1 ||
          this.memberInfo.cbhiCardIssued === '1' ||
          this.memberInfo.cbhi_card_issued === 1;
    },

    cardIssueDate() {
      return this.memberInfo.cardIssueDate || this.memberInfo.card_issue_date;
    },

    cardIssuedBy() {
      return this.memberInfo.cardIssuedBy || this.memberInfo.card_issued_by || 'System';
    },

    cardIssueNotes() {
      return this.memberInfo.cardIssueNotes || this.memberInfo.card_issue_notes;
    },

    totalMembers() {
      return 1 + this.beneficiaryCount;
    }
  },

  mounted() {
    this.cbhiNumber = this.$route.params.cbhi_number;
    this.loadMemberDetails();
  },

  methods: {
    async loadMemberDetails() {
      this.loading = true;

      const data = {
        "function": "getMemberDetails",
        "cbhiNumber": this.cbhiNumber
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          const details = res.data.data;

          this.memberInfo = {
            memberId: details.memberInfo?.memberId,
            cbhiNumber: details.memberInfo?.cbhiNumber,
            fullName: details.memberInfo?.fullName,
            dateOfBirth: details.memberInfo?.dateOfBirth,
            age: details.memberInfo?.age,
            gender: details.memberInfo?.gender,
            nationalId: details.memberInfo?.nationalId,
            profilePhoto: details.memberInfo?.profilePhoto,
            profile_photo: details.memberInfo?.profilePhoto,
            regionName: details.memberInfo?.location?.region,
            zoneName: details.memberInfo?.location?.zone,
            woredaName: details.memberInfo?.location?.woreda,
            kebeleName: details.memberInfo?.location?.kebele,
            expiryDate: details.membershipStatus?.expiryDate,
            status: details.membershipStatus?.status,
            cbhiCardIssued: details.cardInfo?.cbhiCardIssued,
            cardIssueDate: details.cardInfo?.cardIssueDate,
            cardIssuedBy: details.cardInfo?.cardIssuedBy,
            cardIssueNotes: details.cardInfo?.cardIssueNotes
          };

          this.planInfo = {
            plan_id: details.planInfo?.planId,
            plan_name: details.planInfo?.planName,
            plan_code: details.planInfo?.planCode,
            plan_type: details.planInfo?.planType,
            annual_coverage_limit: details.planInfo?.annualCoverageLimit,
            per_visit_limit: details.planInfo?.perVisitLimit
          };

          this.beneficiaryCount = details.householdInfo?.totalBeneficiaries - 1 || 0;

        } else {
          this.showAlert('failed', res.data.message || 'Failed to load member details');
        }
      } catch (e) {
        console.error('Error loading member details:', e);
        this.showAlert('failed', 'Failed to load member details');
      } finally {
        this.loading = false;
      }
    },

    handleMarkAsIssued() {
      const modal = new bootstrap.Modal(document.getElementById('markIssuedModal'));
      modal.show();
    },

    async confirmMarkAsIssued() {
      this.processing = true;

      const data = {
        "function": "markCardAsIssued",
        "cbhiNumber": this.cbhiNumber,
        "issueNotes": this.issueNotes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Card marked as issued successfully');

          // Close modal
          const modal = bootstrap.Modal.getInstance(document.getElementById('markIssuedModal'));
          modal.hide();

          // Reload data
          await this.loadMemberDetails();

          this.issueNotes = '';
        } else {
          this.showAlert('failed', res.data.message || 'Failed to mark card as issued');
        }
      } catch (e) {
        console.error('Error marking card as issued:', e);
        this.showAlert('failed', 'Failed to mark card as issued');
      } finally {
        this.processing = false;
      }
    },

    getStatusBadgeClass() {
      const statusMap = {
        'active': 'bg-success',
        'inactive': 'bg-warning',
        'suspended': 'bg-danger',
        'expired': 'bg-secondary'
      };
      return statusMap[this.memberInfo.status] || 'bg-secondary';
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
/* Additional page-specific styles */
.table-sm td {
  padding: 0.5rem;
}

.alert {
  border-radius: 0.5rem;
}

.modal-header.bg-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%) !important;
}
</style>